'use client'

/**
 * AmazonPhotoLink — the product page hero for a product with no photo of ours (FRIDGE-1666 Step 2).
 *
 * It renders its children (the hero's brand logo chain, FRIDGE-635) and, when `enabled`, asks /api/amazon-photo
 * for Amazon's own current image. When one comes back, the hero shows it on the Amazon licence's terms: from
 * Amazon's own address, never through the image optimiser (which would store a copy), and as a link to the Amazon
 * offer, which opens that item's Amazon page through the site's signed offer link. The page markup carries no
 * Amazon address, because the page is cached for longer than a stored image link may live.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { SignedOfferLink } from './signed-offer-link'

export interface AmazonPhoto {
  offerId: string
  url: string
  width: number | null
  height: number | null
}

const isPhoto = (value: unknown): value is AmazonPhoto => {
  const v = value as Partial<AmazonPhoto> | null
  return !!v && typeof v.offerId === 'string' && typeof v.url === 'string' && /^https:\/\//i.test(v.url)
}

export function AmazonPhotoLink({
  enabled,
  productId,
  productName,
  className,
  children,
}: {
  /** True only when the product has no photo of ours. */
  enabled: boolean
  productId: string | null | undefined
  productName: string
  /** The hero image's own classes, so the photo sits exactly where the fallback does. */
  className?: string
  /** What the hero shows without an Amazon photo. */
  children: ReactNode
}) {
  const [photo, setPhoto] = useState<AmazonPhoto | null>(null)

  useEffect(() => {
    if (!enabled || !productId) return
    const controller = new AbortController()
    fetch(`/api/amazon-photo/${encodeURIComponent(productId)}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (isPhoto(data)) setPhoto(data)
      })
      .catch(() => {
        // No photo: the fallback stays.
      })
    return () => controller.abort()
  }, [enabled, productId])

  if (!enabled || !productId || !photo) return <>{children}</>
  return <AmazonPhotoAnchor photo={photo} productId={productId} productName={productName} className={className} />
}

/** The photo itself: Amazon's address, unoptimised, inside the signed link to its Amazon offer. */
export function AmazonPhotoAnchor({
  photo,
  productId,
  productName,
  className,
}: {
  photo: AmazonPhoto
  productId: string
  productName: string
  className?: string
}) {
  return (
    <SignedOfferLink
      offerId={photo.offerId}
      productId={productId}
      pos="amazon-photo"
      target="_blank"
      className="relative block h-full w-full"
      aria-label={`${productName}. Photo: Amazon. Opens the Amazon page.`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- Amazon's licence allows its image only from its own address; the optimiser would store a copy (FRIDGE-1666) */}
      <img
        src={photo.url}
        alt=""
        width={photo.width ?? 500}
        height={photo.height ?? 500}
        className={className}
        decoding="async"
      />
      <span className="pointer-events-none absolute bottom-2 right-3 rounded bg-white/90 px-1.5 py-0.5 text-xs text-slate-600">
        Photo: Amazon
      </span>
    </SignedOfferLink>
  )
}
